import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Attachment, AttachmentDeleteParams } from '../../../models/attachments';
import { AttachmentMutation } from '../../apiNew';

export type DraftAttachment = Attachment & { pendingFile?: File };

export function normalizeAttachmentForViewer(a: Attachment): Attachment {
  return {
    ...a,
    attachment_file_name: a.attachment_file_name ?? a.file_name,
  };
}

export function buildAttachmentMutations(draft: DraftAttachment[]): AttachmentMutation[] {
  const ops: AttachmentMutation[] = [];
  draft.forEach((d) => {
    // eslint-disable-next-line no-underscore-dangle -- Attachment model / FileViewer
    if (d.id && d._destroy) {
      ops.push({ removeId: d.id });
    }
  });
  draft.forEach((d) => {
    // eslint-disable-next-line no-underscore-dangle -- Attachment model / FileViewer
    if (!d._destroy && d.pendingFile) {
      const f = d.pendingFile;
      ops.push({ file: f, file_name: d.file_name ?? f.name });
    }
  });
  return ops;
}

type UseDraftAttachmentsOptions = {
  mode: 'create' | 'edit';
  initialAttachments?: Attachment[] | null;
};

export default function useDraftAttachments({
  mode,
  initialAttachments,
}: UseDraftAttachmentsOptions): {
  draftAttachments: DraftAttachment[];
  setDraftAttachments: Dispatch<SetStateAction<DraftAttachment[]>>;
  addDraftAttachment: (files: FileList | null) => void;
  handleDeleteClick: (params: AttachmentDeleteParams) => void;
  resetDraftAttachments: (attachments: Attachment[] | null | undefined) => void;
} {
  const [draftAttachments, setDraftAttachments] = useState<DraftAttachment[]>(() =>
    (initialAttachments ?? []).map(normalizeAttachmentForViewer),
  );

  const resetDraftAttachments = useCallback((attachments: Attachment[] | null | undefined) => {
    setDraftAttachments((attachments ?? []).map(normalizeAttachmentForViewer));
  }, []);

  const addDraftAttachment = useCallback((files: FileList | null): void => {
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (): void => {
        setDraftAttachments((prev) => [
          ...prev,
          {
            attachment: reader.result as string,
            attachment_file_name: file.name,
            file_name: file.name,
            content_type: file.type,
            pendingFile: file,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleDeleteClick = useCallback(
    (params: AttachmentDeleteParams): void => {
      const { fileId, fileName } = params;
      if (mode === 'create') {
        setDraftAttachments((prev) =>
          prev.filter((a) => {
            if (fileId && a.id === fileId) return false;
            if (fileName && (a.file_name === fileName || a.attachment_file_name === fileName)) return false;
            return true;
          }),
        );
        return;
      }
      setDraftAttachments((prev) => {
        const match = prev.find(
          (a) =>
            (fileId && a.id === fileId) ||
            (!fileId && (a.file_name === fileName || a.attachment_file_name === fileName)),
        );
        if (!match) return prev;
        if (match.id && !match.pendingFile) {
          return prev.map((a) => {
            if (a.id !== match.id) return a;
            // eslint-disable-next-line no-underscore-dangle -- Attachment model / FileViewer
            return { ...a, _destroy: true } as DraftAttachment;
          });
        }
        return prev.filter((a) => a !== match);
      });
    },
    [mode],
  );

  return {
    draftAttachments,
    setDraftAttachments,
    addDraftAttachment,
    handleDeleteClick,
    resetDraftAttachments,
  };
}
